import React, { useEffect, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ReactComponent as BankingAalytics } from "../../icons/dashboard/bankingAnalytics.svg";
import { ReactComponent as ConsumptionAndGenerationAnalytics } from "../../icons/dashboard/consumptionAndGenerationAnalytics.svg";
import { ReactComponent as DemandForecasting } from "../../icons/dashboard/demandForecasting.svg";
import { ReactComponent as DiscomAnalysis } from "../../icons/dashboard/discomAnalysis.svg";
import { ReactComponent as ExchangeAnalysis } from "../../icons/dashboard/exchangeAnalysis.svg";
import { ReactComponent as GridFrequencyProfile } from "../../icons/dashboard/gridFrequencyProfile.svg";
import { ReactComponent as MarketMonitoring } from "../../icons/dashboard/marketmonitoring.svg";
import { ReactComponent as PowerAtlas } from "../../icons/dashboard/powerAtlas.svg";
import { ReactComponent as PriceForecasting } from "../../icons/dashboard/priceForecasting.svg";
import { ReactComponent as Upload } from "../../icons/upload.svg";

import { getSessionValidity } from "../../Rest_api/RestApi";
import { COLOR_CODES } from "../../common";
import { RuvnlTitle } from "./components";
import { ReactComponent as AnalyticsIcon } from "../../icons/analytics.svg";
import { ReactComponent as AnalyticsSelectedIcon } from "../../icons/analytics-selected.svg";
import { ReactComponent as DashboardIcon } from "../../icons/dashboard.svg";
import { ReactComponent as DashboardSelectedIcon } from "../../icons/dashboard-selected.svg";
import { OverviewDas } from "./Overview/OverviewDas";
import { DynamicDatePicker } from "../../components/DatePicker";

function DemoDashboardItem({
  title,
  Icon,
  to,
}: {
  title: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  to: string;
}) {
  return (
    <NavLink to={`${to}`} className="dashboard-item">
      <Icon
        width={80}
        height={33}
        style={{
          fill: COLOR_CODES.primary,
        }}
      />
      <p>{title}</p>
    </NavLink>
  );
}

export default function DemoDashboard() {
  // Clear some localStorage on mount and validate session
  useEffect(() => {
    localStorage.removeItem("procurement_edit_id");
    getSessionValidity().then((res) => {
      if (res.status === false) {
        localStorage.clear();
        window.location.href = "/login";
      }
    });
  }, []);

  // Use searchParams to get and set the 'tab' query parameter
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "analytics";
  const [selectedTab, setSelectedTab] = useState(initialTab);

  // Sync state with query parameter changes
  useEffect(() => {
    const tab = searchParams.get("tab") || "analytics";
    if (tab !== selectedTab) {
      setSelectedTab(tab);
    }
  }, [searchParams, selectedTab]);

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setSearchParams({ tab });
  };

  const [dates, setDate] = useState({
    startDate: new Date("2025-01-06"),
    endDate: new Date("2025-01-13"),
    key: "selection",
  });

  return (
    <div className="page-wrapper">
      <div className="dashboard-header-between flex justify-between items-center">
        <div className="flex flex-row gap-x-4">
          {selectedTab === "analytics" ? (
            <AnalyticsSelectedIcon
              width={30}
              height={30}
              onClick={() => handleTabChange("analytics")}
            />
          ) : (
            <AnalyticsIcon
              width={30}
              height={30}
              onClick={() => handleTabChange("analytics")}
            />
          )}
          {selectedTab === "dashboard" ? (
            <DashboardSelectedIcon
              width={30}
              height={30}
              onClick={() => handleTabChange("dashboard")}
            />
          ) : (
            <DashboardIcon
              width={30}
              height={30}
              onClick={() => handleTabChange("dashboard")}
            />
          )}
        </div>
        <div>
          <RuvnlTitle />
        </div>
        <div>
          {selectedTab === "analytics" && (
            <DynamicDatePicker
              setDate={(date) => setDate(date[0]!)}
              defaultDates={dates}
            />
          )}
        </div>
      </div>

      {selectedTab === "analytics" ? (
        <div className="page-wrapper">
          <OverviewDas dates={dates} />
        </div>
      ) : (
        <div className="dashboard-item-container">
          <div className="flex flex-col justify-between h-full">
            <div className="flex flex-row flex-wrap gap-5 justify-center">
              <DemoDashboardItem
                Icon={PowerAtlas}
                title="Overview"
                to="compare"
              />
              <DemoDashboardItem
                Icon={DemandForecasting}
                title="Demand Analytics"
                to="demand"
              />
              <DemoDashboardItem
                Icon={ConsumptionAndGenerationAnalytics}
                title="Generation Analytics"
                to="generation"
              />
              <DemoDashboardItem
                Icon={ExchangeAnalysis}
                title="Power Procurement"
                to="procurement"
              />
              <DemoDashboardItem
                Icon={BankingAalytics}
                title="Automation"
                to="automation"
              />
              <DemoDashboardItem
                Icon={Upload}
                title="Upload Data"
                to="upload"
              />
              <DemoDashboardItem
                Icon={GridFrequencyProfile}
                title="Transactions"
                to="procurementTable"
              />
            </div>
            <div className="flex justify-center text-center mt-8 mb-8">
              <h1>
                GNA Energy Data and Analytics Capability Center <br /> (GDACC)
              </h1>
            </div>
          </div>
          <div className="betaText">Beta Version</div>
        </div>
      )}
    </div>
  );
}

export const TabSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Read the current 'tab' query parameter from the URL.
  // Default to "analytics" if no tab is specified.
  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get("tab") || "dashboard";

  // When a tab is clicked, update the URL accordingly.
  const handleTabChange = (newTab: string) => {
    navigate(`/ruvnl?tab=${newTab}`);
  };

  return (
    <div className="flex flex-row gap-x-2">
      {tab === "analytics" ? (
        <AnalyticsSelectedIcon
          width={30}
          height={30}
          onClick={() => handleTabChange("analytics")}
          style={{ cursor: "pointer" }}
        />
      ) : (
        <AnalyticsIcon
          width={30}
          height={30}
          onClick={() => handleTabChange("analytics")}
          style={{ cursor: "pointer" }}
        />
      )}
      {tab === "dashboard" ? (
        <DashboardSelectedIcon
          width={30}
          height={30}
          onClick={() => handleTabChange("dashboard")}
          style={{ cursor: "pointer" }}
        />
      ) : (
        <DashboardIcon
          width={30}
          height={30}
          onClick={() => handleTabChange("dashboard")}
          style={{ cursor: "pointer" }}
        />
      )}
    </div>
  );
};
